import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Users, Award, TrendingUp, ArrowRight } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description:
        'To empower organizations with a reliable, intuitive platform that turns incident chaos into structured, trackable workflows — so issues get resolved faster and customers stay happy.',
    },
    {
      icon: Eye,
      title: 'Our Vision',
      description:
        'A world where every support team, no matter its size, has enterprise-grade tools to detect, manage, and learn from incidents before they impact the business.',
    },
    {
      icon: Heart,
      title: 'Our Values',
      description:
        'Transparency, reliability, and customer obsession. We build software we would want to use ourselves — fast, secure, and honest about what it does.',
    },
  ];

  const stats = [
    { icon: Users, value: '10,000+', label: 'Active Users' },
    { icon: Award, value: '99.9%', label: 'Uptime SLA' },
    { icon: TrendingUp, value: '1M+', label: 'Tickets Resolved' },
  ];

  return (
    <div className="py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">
            About <span className="text-primary-600">Us</span>
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            We build the Incident Management System — a complete platform for tracking,
            managing, and resolving incidents and support tickets. From the first report to
            final resolution, our tools keep your team organized and your customers informed.
          </p>
        </div>

        {/* Story */}
        <div className="mt-16 bg-white rounded-2xl shadow-sm border border-gray-200 p-8 sm:p-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Story</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              The Incident Management System was born from a simple observation: most teams
              handle incidents through a messy mix of emails, spreadsheets, and chat threads.
              Critical issues slip through the cracks, escalations happen too late, and nobody
              has a clear picture of what's going on.
            </p>
            <p>
              We set out to fix that with a single platform that brings together ticketing,
              real-time chat, smart escalation rules, role-based access control, and detailed
              reporting. Today, teams use our system to manage everything from IT outages to
              customer support requests — all in one place.
            </p>
          </div>
        </div>

        {/* Mission / Vision / Values */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {values.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 hover:shadow-md transition-shadow"
              >
                <div className="h-12 w-12 rounded-xl bg-primary-50 flex items-center justify-center mb-5">
                  <Icon className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            );
          })}
        </div>

        {/* Stats */}
        <div className="mt-16 bg-primary-600 rounded-2xl p-8 sm:p-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label}>
                  <Icon className="h-8 w-8 text-primary-200 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white">{stat.value}</div>
                  <div className="mt-1 text-primary-200">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Ready to get started?</h2>
          <p className="mt-3 text-gray-600">
            Join thousands of teams already managing incidents smarter.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-all hover:shadow-lg"
            >
              Create Free Account
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 px-6 py-3 rounded-lg font-medium border border-primary-200 hover:bg-primary-50 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
